/***
 * list of shopping cart items
 * */
import * as React from "react";
import { formatCurrency } from "../../../lib/common";

// only triggers build.  the style sheet must be included on page manually.
import "../cart-widget.scss";

import { CartContext } from "./cart-context";

interface attributeProps {
  attributes: Array<ShoppingCartItemAttribute>;
}

const AttributeList: React.FunctionComponent<attributeProps> = (
  props: attributeProps
) => {
  const attributes = props.attributes;

  const listItems = attributes.map((att, index) => (
    <p key={index}>
      <span className="label">{att.Name}:</span>
      <span className="attribute">{att.Value}</span>
    </p>
  ));

  return <div className="attribute-list">{listItems}</div>;
};

const RemovedCartItemWidget: React.FunctionComponent<{
  item: ShoppingCartItem;
}> = (props: { item: ShoppingCartItem }) => {
  const context = React.useContext(CartContext);
  if (!context) {
    throw new Error("invalid context");
  }
  const cartItem = props.item;

  return (
    <div className="removed-cart-item">
      <p className="name">{props.item.ProductName} has been removed.</p>
      <span
        className="button"
        onClick={() => context.updateCartItem(cartItem, 1)}
      >
        Undo
      </span>
    </div>
  );
};

const showPrice = (cartItem: ShoppingCartItem) => {
  if (cartItem.ItemTotal !== cartItem.Price) {
    return (
      <React.Fragment>
        {formatCurrency(cartItem.Price)} ea.{" "}
        {formatCurrency(cartItem.ItemTotal)} total
      </React.Fragment>
    );
  }

  return formatCurrency(cartItem.Price);
};

const ShoppingCartItemWidget: React.FunctionComponent<{
  item: ShoppingCartItem;
}> = (props: { item: ShoppingCartItem }) => {
  const cartContext = React.useContext(CartContext);

  if (cartContext === undefined) {
    throw new Error(
      `Unable to load cart without context, review calling tree for provider`
    );
  }

  const cartItem = props.item;
  const priceDisplay = showPrice(cartItem);
  let inventoryMessage = "";
  let containerClass = "cart-item ";

  if (!cartItem.InventoryLevel || cartItem.InventoryLevel < cartItem.Quantity) {
    if (cartItem.InventoryLevel && cartItem.InventoryLevel > 0) {
      inventoryMessage = `There are only ${cartItem.InventoryLevel} items available at this location. Please adjust your quantity to continue`;
      containerClass += "low-inventory";
    } else {
      inventoryMessage = "This product is unavailable at this location.";
      containerClass += "no-inventory";
    }

    if (cartItem.needsAddress) {
      inventoryMessage =
        "This product is only available at select partner locations.";
    }
  }

  if (cartItem.Status === "Removed") {
    return (
      <RemovedCartItemWidget key={props.item.ProductId} item={props.item} />
    );
  }

  const selectAddClick = (e: React.MouseEvent) => {
    e.preventDefault();
    cartContext.selectAddress();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter") {
      const target = event.target as Element;
      if (target.className.includes("decrement")) {
        cartContext.updateCartItem(cartItem, cartItem.Quantity - 1);
      } else {
        cartContext.updateCartItem(cartItem, cartItem.Quantity + 1);
      }
    }
  };

  return (
    <div className={containerClass} data-productid={cartItem.ProductId}>
      <div
        className="cart-img-container"
        data-large-img={cartItem.ImageUrl}
        data-title="{cartItem.ProductName}"
      >
        <img src={cartItem.ImageUrl} alt="{cartItem.ProductName}" />
      </div>
      <p className="price">{priceDisplay}</p>

      <div className="quantity">
        <p className="qty-label">Quantity</p>
        <button
          type="button"
          aria-label="Decrease quantity by one"
          className="decrement"
          tabIndex={0}
          onClick={() =>
            cartContext.updateCartItem(cartItem, cartItem.Quantity - 1)
          }
          onKeyUp={handleKeyDown}
        >
          <span className="h"></span>
        </button>
        <span className="totalQty">{cartItem.Quantity}</span>
        <button
          type="button"
          aria-label="Increase quantity by one"
          className="increment"
          tabIndex={0}
          onClick={() =>
            cartContext.updateCartItem(cartItem, cartItem.Quantity + 1)
          }
          onKeyUp={handleKeyDown}
        >
          <span className="h"></span>
          <span className="v"></span>
        </button>
      </div>
      <p className="name">{cartItem.ProductName}</p>
      <p className="sku-container">
        <span className="sku-label">SKU: </span>
        {cartItem.ProductCode}
      </p>

      <AttributeList attributes={cartItem.Attributes} />

      {inventoryMessage && (
        <React.Fragment>
          <p className="error">
            {inventoryMessage}{" "}
            <a
              className="keep-shopping"
              title="Continue Shopping"
              href="/Cart/ContinueShopping"
            >
              Find Another Product
            </a>
          </p>
          {cartItem.needsAddress && (
            <p className="search-for-hospital">
              <a onClick={selectAddClick} href="#">
                Select hospital to confirm product availability
              </a>
            </p>
          )}
          <p
            className="remove-from-cart-button"
            onClick={() => cartContext.updateCartItem(cartItem, 0)}
          >
            <a>Remove Item</a>
          </p>
        </React.Fragment>
      )}
    </div>
  );
};

interface cartProps {
  cart: ShoppingCart;
}

export const ShoppingCartWidget: React.FunctionComponent<cartProps> = (
  props: cartProps
) => {
  return (
    <div className="ShoppingCartList">
      {props.cart.Items.map((item: ShoppingCartItem) => (
        <ShoppingCartItemWidget key={item.ProductId} item={item} />
      ))}
    </div>
  );
};

